/**
 * Utilisateurs actifs par jour — ligne, une seule série.
 *
 * Un « actif » est un compte ayant produit au moins une action ce jour-là. Les
 * actions sans utilisateur rattaché n'entrent pas dans le décompte.
 */

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useChartMarkers } from '../hooks/useChartMarkers';
import { formatCompact, formatDayLong, formatDayShort, formatInteger } from '../lib/format';
import { AXIS_STYLE, COLORS, LINE_WIDTH } from '../lib/theme';
import type { DailyActivityPoint, DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartTooltip } from './ChartTooltip';
import { MarkerStrip } from './MarkerStrip';

const columns: ReadonlyArray<DataColumn<DailyActivityPoint>> = [
  { key: 'date', header: 'Jour', render: (row) => formatDayLong(row.date) },
  {
    key: 'activeUsers',
    header: 'Actifs',
    numeric: true,
    render: (row) => formatInteger(row.activeUsers),
  },
];

/** Série de flux : le nombre d'actifs d'un jour ne contient pas celui de la veille. */
const MARKER_CONFIG = {
  value: (row: DailyActivityPoint) => row.activeUsers,
  mode: 'level',
  unit: 'count',
  higherIsBetter: true,
  scope: 'analytics',
} as const;

export function ActiveUsersChart({ data }: { data: readonly DailyActivityPoint[] }) {
  const markers = useChartMarkers(data, MARKER_CONFIG);

  return (
    <ChartCard
      title="Utilisateurs actifs par jour"
      subtitle="Comptes distincts ayant produit au moins une action"
      rows={data}
      columns={columns}
      footer={<MarkerStrip items={markers.items} />}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data as DailyActivityPoint[]} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={formatDayShort}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={{ stroke: COLORS.grid }}
            tickLine={false}
            minTickGap={40}
          />
          <YAxis
            tickFormatter={formatCompact}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <Tooltip
            cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
            content={
              <ChartTooltip labelFormatter={formatDayLong} valueFormatter={formatInteger} />
            }
          />
          {markers.lines}
          <Line
            isAnimationActive={false}
            type="monotone"
            dataKey="activeUsers"
            name="Utilisateurs actifs"
            stroke={COLORS.series1}
            strokeWidth={LINE_WIDTH}
            dot={false}
            activeDot={{ r: 4, fill: COLORS.series1, stroke: COLORS.surface, strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
